import { APIError, useClientPortalAPI, useSubmit } from "@certn/api";
import { PrimaryButton } from "@certn/cascadia/components/button/PrimaryButton";
import { BasicButton, Link, Stack, Text } from "@certn/cascadia";
import {
  CheckboxFormikField,
  Divider,
  Radio,
  RadioGroupFormikField,
  SectionMessage,
  TextAreaFormikField,
  TextInputFormikField,
  showToast,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import {
  Form,
  Formik,
  FieldArray,
  useFormikContext,
  FormikProps,
} from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { boolean, object, string, array } from "yup";

import { useUser } from "../../../hooks/useUser";
import { ApplicantSuppliedDetailsPanel } from "./ApplicantSuppliedDetailsPanel";
import { IdentityOnDocumentSection } from "./IdentityOnDocumentSection";
import { ManualIdvDeclineConfirmationModal } from "./ManualIdvDeclineConfirmationModal";
import {
  DocumentEntry,
  DocumentEntryForm,
  SavedDocumentCard,
} from "./ManualIdvDocumentEntry";
import {
  areManualIdvRouteRequirementsMet,
  resolveManualIdvDocumentValidationProfile,
  useManualIdvRouteRules,
  type ManualIdvDocumentValidationProfile,
} from "./manualIdvRouteRules";

interface IdentityVerificationFormProps {
  caseId?: string;
  hasNotManuallyVerified?: boolean;
  updateTabAndCaseDetails: () => void;
}

interface ManualIdvIdentityOnDocument {
  title: string;
  given_name: string;
  additional_name: string;
  family_name: string;
  date_of_birth: string;
}

interface FormValues {
  identity_manually_verified: boolean;
  name_of_verifier: string;
  manual_idv_route_used: string;
  manual_idv_document_review_method: string;
  manual_idv_not_in_person_reason: string;
  manual_idv_option2_not_practical_reason: string;
  manual_idv_identity_on_document: ManualIdvIdentityOnDocument;
  documents: DocumentEntry[];
}

const DBS_GUIDELINES_BASE_URL =
  "https://www.gov.uk/government/publications/dbs-identity-checking-guidelines/id-checking-guidelines-for-standardenhanced-dbs-check-applications-from-22-april-2025";

const DBS_HOW_TO_VIEW_URL = `${DBS_GUIDELINES_BASE_URL}#where-to-go-for-help-to-check-non-uk-issued-identity-and-travel-documents`;
const DBS_ROUTE_CHECKING_URL = `${DBS_GUIDELINES_BASE_URL}#the-3-route-id-checking-process`;
const DBS_DOCUMENT_LISTS_URL = `${DBS_GUIDELINES_BASE_URL}#document-lists`;

export function IdentityVerificationForm(props: IdentityVerificationFormProps) {
  const { caseId, hasNotManuallyVerified, updateTabAndCaseDetails } = props;
  const { details } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    data: rawData,
    isLoading,
    mutate: updateIdentityVerificationDetails,
  } = useClientPortalAPI(
    `/api/internal/client-portal/case/${caseId}/identification-verified-details/`
  );
  const data = rawData as Record<string, unknown> | undefined;

  const { submit } = useSubmit(
    `/api/internal/client-portal/case/${caseId}/manual-identification-verification/`,
    { throwErrors: true, showToastOnError: true }
  );

  const { submit: declineSubmit } = useSubmit(
    `/api/internal/client-portal/case/${caseId}/manual-idv-decline/`,
    { throwErrors: true, showToastOnError: true }
  );

  const formConfig = (data?.form_config ?? {}) as Record<
    string,
    boolean | string
  >;
  const requiresReviewMethod = Boolean(formConfig.requires_review_method);
  const requiresRoute = Boolean(formConfig.requires_route);
  const requiresDocuments = Boolean(formConfig.requires_documents);
  const requiresRouteValidation = Boolean(formConfig.requires_route_validation);
  const requiresApplicantIdentityOnDocument = Boolean(
    formConfig.requires_applicant_identity_on_document
  );
  const documentValidationProfile =
    resolveManualIdvDocumentValidationProfile(formConfig);
  const applicantSuppliedDetails = data?.applicant_supplied_details as
    | Record<string, string | null>
    | undefined;
  const isAlreadyVerified =
    Boolean(data?.identity_manually_verified) &&
    Boolean(data?.name_of_verifier);
  const existingReason = String(data?.manual_idv_not_in_person_reason || "");
  const existingOption2Reason = String(
    data?.manual_idv_option2_not_practical_reason || ""
  );

  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  if (isLoading || !data) return null;

  const identityOnDocument = data.manual_idv_identity_on_document as
    | Partial<ManualIdvIdentityOnDocument>
    | undefined;

  const initialValues: FormValues = {
    identity_manually_verified:
      Boolean(data?.identity_manually_verified) ||
      !hasNotManuallyVerified ||
      false,
    name_of_verifier: String(
      data?.name_of_verifier || details?.auth0_full_name || ""
    ),
    manual_idv_route_used: String(data?.manual_idv_route_used || ""),
    manual_idv_document_review_method: String(
      data?.manual_idv_document_review_method || ""
    ),
    manual_idv_not_in_person_reason: existingReason,
    manual_idv_option2_not_practical_reason: existingOption2Reason,
    manual_idv_identity_on_document: {
      title: String(identityOnDocument?.title || ""),
      given_name: String(identityOnDocument?.given_name || ""),
      additional_name: String(identityOnDocument?.additional_name || ""),
      family_name: String(identityOnDocument?.family_name || ""),
      date_of_birth: String(identityOnDocument?.date_of_birth || ""),
    },
    documents: isAlreadyVerified
      ? ((data?.documents as Array<Record<string, string>>) || []).map(
          (doc) => ({
            document_group: doc.document_group || "",
            document_type: doc.document_type || "",
            file_key: doc.file_key || "",
            file_name: doc.file_name || "",
            saved: true,
            download_url: doc.download_url || "",
          })
        )
      : [],
  };

  const schema = object({
    identity_manually_verified: boolean(),
    name_of_verifier: string(),
    manual_idv_route_used: requiresRoute
      ? string().required(t("Identity checking route is required."))
      : string(),
    manual_idv_document_review_method: requiresReviewMethod
      ? string().required(t("Document review method is required."))
      : string(),
    manual_idv_not_in_person_reason: string().when(
      "manual_idv_document_review_method",
      {
        is: (val: string) =>
          val === "VIDEO_CALL_WITH_PHYSICAL" ||
          val === "VIDEO_CALL_WITHOUT_PHYSICAL",
        then: (s) =>
          s.required(
            t("Please explain why an in-person check was not practical.")
          ),
      }
    ),
    manual_idv_option2_not_practical_reason: string().when(
      "manual_idv_document_review_method",
      {
        is: (val: string) => val === "VIDEO_CALL_WITHOUT_PHYSICAL",
        then: (s) =>
          s.required(t("Please explain why Option 2 was not practical.")),
      }
    ),
    documents: requiresDocuments
      ? array().of(
          object({
            saved: boolean(),
            document_group: string().when("saved", {
              is: true,
              then: (s) => s.required(t("Document group is required.")),
            }),
            document_type: string().when("saved", {
              is: true,
              then: (s) => s.required(t("Document type is required.")),
            }),
            file_key: string().when("saved", {
              is: true,
              then: (s) => s.required(t("Document upload is required.")),
            }),
            file_name: string().when("saved", {
              is: true,
              then: (s) => s.required(),
            }),
          })
        )
      : array(),
    manual_idv_identity_on_document: requiresApplicantIdentityOnDocument
      ? object({
          title: string().required(t("Title is required.")),
          given_name: string().required(t("Given name is required.")),
          additional_name: string(),
          family_name: string().required(t("Family name is required.")),
          date_of_birth: string().required(t("Date of birth is required.")),
        })
      : object(),
  });

  const handleDeclineConfirm = async () => {
    setIsDeclining(true);
    try {
      await declineSubmit({});
      setIsDeclineModalOpen(false);
      showToast(t("Manual ID verification has been declined."), {
        type: "success",
      });
      await updateTabAndCaseDetails();
      navigate(`/case-details/${caseId}`);
    } catch (e) {
      console.error(e);
    } finally {
      setIsDeclining(false);
    }
  };

  return (
    <>
      <ManualIdvDeclineConfirmationModal
        isOpen={isDeclineModalOpen}
        onClose={() => setIsDeclineModalOpen(false)}
        onConfirm={handleDeclineConfirm}
        isConfirmLoading={isDeclining}
      />
      <Formik<FormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={async (values, helpers) => {
          try {
            const payload: Record<string, unknown> = {
              ...values,
              documents: values.documents
                .filter((doc) => doc.saved)
                .map(({ saved: _, ...rest }) => rest),
            };
            if (!requiresApplicantIdentityOnDocument) {
              delete payload.manual_idv_identity_on_document;
            }
            await submit(payload);
            showToast(
              t("Success. {{name}} has been saved as manual verifier.", {
                name: values.name_of_verifier,
              }),
              { type: "success" }
            );
            await updateIdentityVerificationDetails();
            await updateTabAndCaseDetails();
          } catch (e) {
            console.error(e);
            if (e instanceof APIError) {
              helpers.setErrors(e.body as object);
            }
          }
        }}
      >
        {(formik: FormikProps<FormValues>) => {
          const { isSubmitting, values } = formik;
          const isFormDisabled =
            isSubmitting || isAlreadyVerified || !hasNotManuallyVerified;

          return (
            <Form noValidate>
              <ScrollToFirstError />
              <Stack gap="space-6">
                {requiresApplicantIdentityOnDocument && (
                  <>
                    <ApplicantSuppliedDetailsPanel
                      details={applicantSuppliedDetails}
                    />
                    <IdentityOnDocumentSection isDisabled={isFormDisabled} />
                    <Divider />
                  </>
                )}
                {isAlreadyVerified && (
                  <SectionMessage type="success" icon="check_circle">
                    {t("Verified on {{date}} by {{name}}", {
                      date: data?.date_of_verification
                        ? new Date(
                            String(data.date_of_verification) + "T00:00:00"
                          ).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "",
                      name: String(data?.name_of_verifier || ""),
                    })}
                  </SectionMessage>
                )}

                {requiresReviewMethod && (
                  <DocumentReviewMethodSection
                    isDisabled={isFormDisabled}
                    method={values.manual_idv_document_review_method}
                  />
                )}

                {requiresReviewMethod && <Divider />}

                {requiresRoute && (
                  <IdentityCheckingRouteSection
                    isDisabled={isFormDisabled}
                    documentValidationProfile={documentValidationProfile}
                  />
                )}

                {requiresRoute && <Divider />}

                {requiresDocuments && (
                  <DocumentCollectionSection
                    formik={formik}
                    isDisabled={isFormDisabled}
                    caseId={caseId}
                    route={values.manual_idv_route_used}
                    documentValidationProfile={documentValidationProfile}
                  />
                )}

                {requiresDocuments && <Divider />}

                <VerificationConfirmationSection
                  isDisabled={isFormDisabled}
                  isAlreadyVerified={isAlreadyVerified}
                  nameOfVerifier={data?.name_of_verifier as string | undefined}
                  hasNotManuallyVerified={hasNotManuallyVerified}
                  isSubmitting={isSubmitting}
                  values={values}
                  showDeclineButton={requiresDocuments}
                  onDecline={() => setIsDeclineModalOpen(true)}
                  documentRequirementsMet={
                    !requiresRouteValidation ||
                    areManualIdvRouteRequirementsMet(
                      values.documents,
                      values.manual_idv_route_used,
                      documentValidationProfile
                    )
                  }
                />
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

export function getFirstErrorPath(
  errors: unknown,
  prefix = ""
): string | undefined {
  if (typeof errors === "string") {
    return prefix || undefined;
  }

  if (Array.isArray(errors)) {
    for (let index = 0; index < errors.length; index += 1) {
      const path = getFirstErrorPath(
        errors[index],
        prefix ? `${prefix}.${index}` : String(index)
      );
      if (path) {
        return path;
      }
    }
    return undefined;
  }

  if (errors && typeof errors === "object") {
    for (const [key, value] of Object.entries(errors)) {
      const path = getFirstErrorPath(value, prefix ? `${prefix}.${key}` : key);
      if (path) {
        return path;
      }
    }
  }

  return undefined;
}

function ScrollToFirstError() {
  const { submitCount, isValid, errors } = useFormikContext();

  useEffect(() => {
    if (submitCount > 0 && !isValid) {
      const firstErrorPath = getFirstErrorPath(errors);
      if (!firstErrorPath) return;

      const el =
        document.querySelector(`[name="${firstErrorPath}"]`) ||
        document.querySelector(`[data-testid="${firstErrorPath}"]`) ||
        document.querySelector(`[data-testid*="${firstErrorPath}"]`);

      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [submitCount, isValid, errors]);

  return null;
}

function ReviewMethodOption({
  value,
  label,
  description,
  isSelected,
  isDisabled,
  onSelect,
  children,
}: {
  value: string;
  label: string;
  description: string;
  isSelected: boolean;
  isDisabled: boolean;
  onSelect: (value: string) => void;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <label className={styles.radioLabel}>
        <input
          type="radio"
          name="manual_idv_document_review_method"
          value={value}
          checked={isSelected}
          disabled={isDisabled}
          onChange={() => onSelect(value)}
          className={styles.radioInput}
        />
        <div className={styles.radioContent}>
          <Text
            style="input/label"
            color={isDisabled ? "text-disabled" : undefined}
          >
            {label}
          </Text>
          <Text style="input/helper" color="text-weak">
            {description}
          </Text>
        </div>
      </label>
      {isSelected && children && (
        <div className={styles.inlineFields}>{children}</div>
      )}
    </div>
  );
}

function DocumentReviewMethodSection({
  isDisabled,
  method,
}: {
  isDisabled: boolean;
  method: string;
}) {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext<FormValues>();

  const selectMethod = (value: string) => {
    setFieldValue("manual_idv_document_review_method", value);
    if (value === "IN_PERSON") {
      setFieldValue("manual_idv_not_in_person_reason", "");
      setFieldValue("manual_idv_option2_not_practical_reason", "");
    } else if (value === "VIDEO_CALL_WITH_PHYSICAL") {
      setFieldValue("manual_idv_option2_not_practical_reason", "");
    }
  };

  return (
    <div>
      <Stack gap="space-2">
        <Text style="global/heading-large">
          {t("How did you check the applicant's identity documents?")}
        </Text>
        <Text style="global/body-secondary" color="text-weak">
          {t(
            "There are three ways that identity documents can be viewed and validated. You must use option 1 unless it is not practical to have a face-to-face appointment with the applicant."
          )}{" "}
          <Link
            linkStyle="monochrome"
            textStyle="global/body-secondary-link"
            textColor="text-weak"
            href={DBS_HOW_TO_VIEW_URL}
            testID="document-review-learn-more-link"
            isNewTab
          >
            {t("Learn more")}
          </Link>
        </Text>
      </Stack>

      <div className={styles.sectionContent}>
        <Stack gap="space-4">
          <ReviewMethodOption
            value="IN_PERSON"
            label={t("Option 1 — In person")}
            description={t(
              "You viewed the original documents in the presence of the applicant."
            )}
            isSelected={method === "IN_PERSON"}
            isDisabled={isDisabled}
            onSelect={selectMethod}
          />
          <ReviewMethodOption
            value="VIDEO_CALL_WITH_PHYSICAL"
            label={t("Option 2 — Video call with physical documents")}
            description={t(
              "The identity check was completed via video call and you were in possession of the original documents."
            )}
            isSelected={method === "VIDEO_CALL_WITH_PHYSICAL"}
            isDisabled={isDisabled}
            onSelect={selectMethod}
          >
            <TextAreaFormikField
              testID="manual-idv-not-in-person-reason"
              name="manual_idv_not_in_person_reason"
              label={t("Why was an in-person check not practical?")}
              isRequired
              isDisabled={isDisabled}
            />
          </ReviewMethodOption>
          <ReviewMethodOption
            value="VIDEO_CALL_WITHOUT_PHYSICAL"
            label={t("Option 3 — Video call without physical documents")}
            description={t(
              "The documents were shown via video call or sent electronically and the originals will be checked later."
            )}
            isSelected={method === "VIDEO_CALL_WITHOUT_PHYSICAL"}
            isDisabled={isDisabled}
            onSelect={selectMethod}
          >
            <TextAreaFormikField
              testID="manual-idv-not-in-person-reason-opt3"
              name="manual_idv_not_in_person_reason"
              label={t("Why was an in-person check not practical?")}
              isRequired
              isDisabled={isDisabled}
            />
            <TextAreaFormikField
              testID="manual-idv-option2-not-practical-reason"
              name="manual_idv_option2_not_practical_reason"
              label={t("Why was Option 2 not practical?")}
              isRequired
              isDisabled={isDisabled}
            />
          </ReviewMethodOption>
        </Stack>
      </div>
    </div>
  );
}

function IdentityCheckingRouteSection({
  isDisabled,
  documentValidationProfile,
}: {
  isDisabled: boolean;
  documentValidationProfile: ManualIdvDocumentValidationProfile;
}) {
  const { t } = useTranslation();
  const routeRules = useManualIdvRouteRules()[documentValidationProfile];

  return (
    <div>
      <Stack gap="space-2">
        <Text style="global/heading-large">
          {t("Which identity checking route was used?")}
        </Text>
        <Text style="global/body-secondary" color="text-weak">
          {routeRules.routesIntro}{" "}
          <Link
            linkStyle="monochrome"
            textStyle="global/body-secondary-link"
            textColor="text-weak"
            href={DBS_ROUTE_CHECKING_URL}
            testID="identity-route-learn-more-link"
            isNewTab
          >
            {t("Learn more")}
          </Link>
        </Text>
      </Stack>

      <div className={styles.sectionContent}>
        <RadioGroupFormikField
          testID="identity-checking-route"
          name="manual_idv_route_used"
          orientation="vertical"
          isRequired
          isDisabled={isDisabled}
        >
          {routeRules.availableRoutes.map((routeId) => {
            const copy = routeRules.routeCopy[routeId];
            return (
              <Radio
                key={routeId}
                testID={`route-${routeId.split("_")[1]}`}
                value={routeId}
                description={copy.description}
              >
                {copy.label}
              </Radio>
            );
          })}
        </RadioGroupFormikField>
      </div>
    </div>
  );
}

function DocumentCollectionSection({
  formik,
  isDisabled,
  caseId,
  route,
  documentValidationProfile,
}: {
  formik: FormikProps<FormValues>;
  isDisabled: boolean;
  caseId?: string;
  route: string;
  documentValidationProfile: ManualIdvDocumentValidationProfile;
}) {
  const { t } = useTranslation();
  const documents = formik.values.documents;
  const routeRules = useManualIdvRouteRules()[documentValidationProfile];
  const routeRequirements =
    routeRules.routeRequirementsInfo[
      route as keyof typeof routeRules.routeRequirementsInfo
    ];

  return (
    <div>
      <Stack gap="space-2">
        <Text style="global/heading-large">
          {t("Documents used to verify identity")}
        </Text>
        <Text style="global/body-secondary" color="text-weak">
          {t(
            "Check the applicant's original identity documents and record the documents used to verify their identity. The combination of documents must meet the requirements for the route you selected."
          )}{" "}
          <Link
            linkStyle="monochrome"
            textStyle="global/body-secondary-link"
            textColor="text-weak"
            href={DBS_DOCUMENT_LISTS_URL}
            testID="documents-learn-more-link"
            isNewTab
          >
            {t("Learn more")}
          </Link>
        </Text>
      </Stack>

      <div className={styles.sectionContent}>
        <div className={styles.halfWidth}>
          {routeRequirements && (
            <SectionMessage type="info" icon="info">
              <Text style="global/body-secondary">
                <strong>{routeRequirements.title}</strong>
              </Text>
              <ul className={styles.requirementsList}>
                {routeRequirements.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </SectionMessage>
          )}
        </div>

        <FieldArray name="documents">
          {({ push, remove }) => (
            <Stack gap="space-4">
              {documents.map((doc, index) =>
                doc.saved ? (
                  <div key={index} className={styles.halfWidth}>
                    <SavedDocumentCard
                      index={index}
                      onEdit={() =>
                        formik.setFieldValue(`documents.${index}.saved`, false)
                      }
                      isReadOnly={isDisabled}
                      downloadUrl={doc.download_url}
                    />
                  </div>
                ) : (
                  <div key={index} className={styles.halfWidth}>
                    <DocumentEntryForm
                      index={index}
                      caseId={caseId}
                      isDisabled={isDisabled}
                      onRemove={() => remove(index)}
                      onSave={() =>
                        formik.setFieldValue(`documents.${index}.saved`, true)
                      }
                    />
                  </div>
                )
              )}
              <div>
                <BasicButton
                  testID="add-document-button"
                  onPress={() =>
                    push({
                      document_group: "",
                      document_type: "",
                      file_key: "",
                      file_name: "",
                      saved: false,
                    })
                  }
                  isDisabled={isDisabled}
                >
                  {t("+ Add document")}
                </BasicButton>
              </div>
            </Stack>
          )}
        </FieldArray>

        <RouteRequirementsChecklist
          documents={documents}
          route={route}
          documentValidationProfile={documentValidationProfile}
        />
      </div>
    </div>
  );
}

export function RouteRequirementsChecklist({
  documents,
  route,
  documentValidationProfile,
}: {
  documents: DocumentEntry[];
  route: string;
  documentValidationProfile: ManualIdvDocumentValidationProfile;
}) {
  const { t } = useTranslation();
  const saved = documents.filter((d) => d.saved);
  const group1Count = saved.filter(
    (d) => d.document_group === "GROUP_1"
  ).length;
  const group2aCount = saved.filter(
    (d) => d.document_group === "GROUP_2A"
  ).length;
  const totalCount = saved.length;

  if (route === "ROUTE_1") {
    if (documentValidationProfile === "basic") {
      const additionalCount = group1Count >= 1 ? totalCount - 1 : totalCount;
      const additionalMet = additionalCount >= 1;
      const additionalNeeded = Math.max(0, 1 - additionalCount);
      return (
        <ul className={styles.checklist}>
          <RequirementItem
            met={group1Count >= 1}
            text={
              group1Count >= 1
                ? t("1 document from Group 1")
                : t("Requires 1 document from Group 1")
            }
          />
          <RequirementItem
            met={additionalMet}
            text={
              additionalMet
                ? t("1 additional document from Group 1, 2a or 2b")
                : t(
                    "{{count}} additional document(s) needed from Group 1, 2a or 2b",
                    { count: additionalNeeded }
                  )
            }
          />
        </ul>
      );
    }
    const additionalCount = group1Count >= 1 ? totalCount - 1 : totalCount;
    const additionalMet = additionalCount >= 2;
    const additionalNeeded = Math.max(0, 2 - additionalCount);
    return (
      <ul className={styles.checklist}>
        <RequirementItem
          met={group1Count >= 1}
          text={
            group1Count >= 1
              ? t("1 document from Group 1")
              : t("Requires 1 document from Group 1")
          }
        />
        <RequirementItem
          met={additionalMet}
          text={
            additionalMet
              ? t("2 additional documents from Group 1, 2a or 2b")
              : t(
                  "{{count}} additional document(s) needed from Group 1, 2a or 2b",
                  { count: additionalNeeded }
                )
          }
        />
      </ul>
    );
  }
  if (route === "ROUTE_2") {
    if (documentValidationProfile === "basic") {
      const hasGroup1 = group1Count > 0;
      const furtherNeeded = Math.max(
        0,
        2 - (totalCount - Math.min(group2aCount, 1))
      );
      return (
        <ul className={styles.checklist}>
          <RequirementItem
            met={!hasGroup1}
            text={
              hasGroup1
                ? t("Route 2 cannot include a Group 1 document")
                : t("No Group 1 documents")
            }
          />
          <RequirementItem
            met={group2aCount >= 1}
            text={t("1 document from Group 2a")}
          />
          <RequirementItem
            met={totalCount >= 3}
            text={
              furtherNeeded > 0
                ? t("{{count}} further document(s) from Group 2a or 2b", {
                    count: furtherNeeded,
                  })
                : t("2 further documents from Group 2a or 2b")
            }
          />
        </ul>
      );
    }
    const additionalNeeded = Math.max(
      0,
      2 - (totalCount - Math.min(group2aCount, 1))
    );
    return (
      <ul className={styles.checklist}>
        <RequirementItem
          met={group2aCount >= 1}
          text={t("1 document from Group 2a")}
        />
        <RequirementItem
          met={totalCount >= 3}
          text={
            additionalNeeded > 0
              ? t("{{count}} additional document(s)", {
                  count: additionalNeeded,
                })
              : t("2 additional documents")
          }
        />
        <RequirementItem
          met={false}
          text={t("An external identity validation check")}
        />
      </ul>
    );
  }
  if (
    route === "ROUTE_3" &&
    documentValidationProfile === "standard_enhanced"
  ) {
    const hasBirthCert = saved.some(
      (d) => d.document_type === "BIRTH_CERTIFICATE_AFTER_12_MONTHS"
    );
    const furtherNeeded = Math.max(
      0,
      3 - (totalCount - (hasBirthCert ? 1 : 0) - Math.min(group2aCount, 1))
    );
    return (
      <ul className={styles.checklist}>
        <RequirementItem
          met={hasBirthCert}
          text={t("A birth certificate (issued after 12 months)")}
        />
        <RequirementItem
          met={group2aCount >= 1}
          text={t("1 document from Group 2a")}
        />
        <RequirementItem
          met={totalCount >= 5}
          text={
            furtherNeeded > 0
              ? t("{{count}} further document(s) from Group 2a or 2b", {
                  count: furtherNeeded,
                })
              : t("3 further documents from Group 2a or 2b")
          }
        />
      </ul>
    );
  }
  return null;
}

function RequirementItem({ met, text }: { met: boolean; text: string }) {
  return (
    <li className={styles.requirementItem}>
      <span
        className={css(
          styles.requirementIcon,
          met ? styles.requirementIconMet : styles.requirementIconUnmet
        )}
      >
        {met ? "\u2713" : "\u2022"}
      </span>
      <Text
        style="global/body-secondary"
        color={met ? "text-success" : "text-weak"}
      >
        {text}
      </Text>
    </li>
  );
}

function VerificationConfirmationSection({
  isDisabled,
  isAlreadyVerified,
  nameOfVerifier,
  hasNotManuallyVerified,
  isSubmitting,
  values,
  showDeclineButton,
  onDecline,
  documentRequirementsMet,
}: {
  isDisabled: boolean;
  isAlreadyVerified: boolean;
  nameOfVerifier?: string;
  hasNotManuallyVerified?: boolean;
  isSubmitting: boolean;
  values: FormValues;
  showDeclineButton: boolean;
  onDecline: () => void;
  documentRequirementsMet: boolean;
}) {
  const { t } = useTranslation();

  const isSubmitDisabled =
    isAlreadyVerified ||
    !values.identity_manually_verified ||
    !values.name_of_verifier ||
    !hasNotManuallyVerified ||
    !documentRequirementsMet;

  return (
    <div>
      <CheckboxFormikField
        aria-label="identity-verification-select-button"
        className={styles.identityVerifiedStyles}
        name="identity_manually_verified"
        isRequired
        isDisabled={isDisabled}
        testID="identity-verification-select-button"
      >
        {t(
          "I confirm that I have verified the applicant's identity in line with DBS guidance"
        )}
      </CheckboxFormikField>
      {values.identity_manually_verified && (
        <div className={styles.nameOfVerifierStyles}>
          <TextInputFormikField
            name="name_of_verifier"
            label={t("Name of verifier")}
            isRequired
            isDisabled={
              isSubmitting || Boolean(nameOfVerifier) || !hasNotManuallyVerified
            }
            testID="identity-verification-name-of-verifier-text-field"
          />
        </div>
      )}
      {!isAlreadyVerified && (
        <Stack gap="space-3" orientation="horizontal">
          <PrimaryButton
            type="submit"
            isLoading={isSubmitting}
            isDisabled={isSubmitDisabled}
            testID="identity-verification-submit-button"
          >
            {t("Confirm")}
          </PrimaryButton>
          {showDeclineButton && hasNotManuallyVerified && (
            <BasicButton
              testID="identity-verification-decline-button"
              onPress={onDecline}
              isDisabled={isSubmitting}
            >
              {t("Decline verification")}
            </BasicButton>
          )}
        </Stack>
      )}
    </div>
  );
}

const styles = {
  identityVerifiedStyles: css({ paddingBottom: "var(--space-6)" }),
  nameOfVerifierStyles: css({ paddingBottom: "var(--space-6)", width: "25%" }),
  sectionContent: css({
    paddingTop: "var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  radioLabel: css({
    display: "flex",
    alignItems: "flex-start",
    gap: "var(--space-3)",
    cursor: "pointer",
  }),
  radioInput: css({
    marginTop: "2px",
    flexShrink: 0,
    width: "18px",
    height: "18px",
    accentColor: "black",
  }),
  radioContent: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-05)",
  }),
  inlineFields: css({
    paddingLeft: "calc(18px + var(--space-3))",
    paddingTop: "var(--space-3)",
    maxWidth: "480px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  halfWidth: css({
    maxWidth: "480px",
    width: "100%",
  }),
  checklist: css({
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  requirementItem: css({
    listStyle: "none",
    display: "flex",
    alignItems: "baseline",
    gap: "var(--space-2)",
  }),
  requirementIcon: css({
    width: "16px",
    textAlign: "center",
    flexShrink: 0,
    fontSize: "14px",
    lineHeight: 1,
  }),
  requirementIconMet: css({
    color: "var(--color-text-success, #16a34a)",
  }),
  requirementIconUnmet: css({
    color: "var(--color-text-weak, #999)",
    fontSize: "18px",
  }),
  requirementsList: css({
    margin: "var(--space-1) 0 0 var(--space-4)",
    padding: 0,
    listStyleType: "disc",
  }),
};
